import React, { useState, useEffect, forwardRef } from "react";
/*
SearchGroup 동작 요약 (정리)

- 기본 데이터 흐름
  1) defaultOption: 초기 로드(또는 전체 초기화) 시 받은 전체 옵션 스냅샷. 선택이 하나도 없을 때 렌더 소스.
  2) searchOptions: 사용자가 옵션을 선택/제거할 때마다 현재 선택(formValues)으로 API 재호출하여 갱신. 선택이 하나 이상일 때 기본 렌더 소스.
  3) previewOptions[key]: '자기 자신'에 선택이 있고 해당 항목을 열었을 때, 자기 자신을 제외한 파라미터로 API를 조건부 호출해 받은 프리뷰.
     - 프리뷰가 준비되기 전에는 searchOptions로 먼저 표시했다가 도착 후 교체하여 후보가 갑자기 좁아 보이지 않도록 처리.

- 오픈/닫힘
  - 드롭다운/아코디언을 열어도 기본적으로 API를 재호출하지 않음.
  - 단, '자기 자신'에 선택이 있는 경우에만 self-excluded 프리뷰를 조건부 호출(시그니처 캐시로 중복 방지).
  - 아코디언의 onToggle을 부모로 올려 open/close 로그와 상태를 일관 관리.

- 라벨 유지
  - labelDict(전역 라벨 캐시)에 매 호출 응답을 누적 저장하여 선택 태그가 항상 label로 보이도록 함.
  - 선택 태그 라벨은 options → defaultOption → searchOptions → labelDict 순으로 폴백.

- 로딩 처리
  - previewLoading[key]: 프리뷰 호출 중 해당 키의 옵션 영역에 '불러오는 중...' 표시.
  - searchLoading: 전체 필터 재조회 중에는 선택 태그의 'unavailable-selected' 표시를 일시 보류(깜빡임 방지).

- '선택 불가' 표시
  - hasOtherSelection && !searchLoading && !isAvailable 일 때만 태그에 unavailable-selected 클래스 부여.

- 전체 초기화(handleResetClick)
  - formValues 비움 + open/preview 상태 초기화
  - fetchSearchOptions(empty, { replaceDefault: true })로 defaultOption을 최신 전체 옵션으로 재설정

- 렌더 소스 결정(getOptionsForKey)
  - 선택 없음: defaultOption
  - 선택 있음: 기본은 searchOptions
  - (열림 && 자기 자신 선택)인 경우: previewOptions[key]가 있으면 사용, 없으면 searchOptions
*/
import { useNavigate, useSearchParams } from "react-router-dom";
import SearchDropdown from "./searchDropDown";
import "../../css/Home/searchGroup.css";
import { SearchBtn, SearchW, Reset } from "../../assets/icons";
import { apiGet } from "../../utils/api";
import CommonModal from "../Common-components/Modal/modal";
import SearchAccordion from "./searchAccordion";
import { ReactComponent as ResetIcon } from '../../assets/icons/svg/reset.svg'; // 미사용
import Loading from "../Common-components/loading";

const RANGE_SELECT_KEYS = new Set(['startdate', 'spreadnights']);

const SearchGroup = forwardRef(({ moTitle, forceMobile = false, moIconType = 'search' }, ref) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // 초기 검색 옵션 저장용
  const [defaultOption, setDefaultOption] = useState({
    cruise_lines: [],
    destinations: [],
    departure_ports: [],
    ports: [],
    spreadnights: [],
    months: [],
    ship: [],
  });

  // 검색 파라미터 state
  const [searchOptions, setSearchOptions] = useState({
    cruise_lines: [],
    destinations: [],
    departure_ports: [],
    ports: [],
    spreadnights: [],
    months: [],
    ship: [],
  });

  const createEmptyFormValues = () => ({
    destination: [],
    departure: [],
    port: [],
    startdate: [],
    spreadnights: [],
    lineid: [],
    shipid: [],
  });

  const hasAnySelectedValues = (values) => (
    Object.values(values || {}).some(arr => Array.isArray(arr) && arr.length > 0)
  );

  // ✅ formValues를 배열 기반으로 변경 (다중 선택 지원)
  const [formValues, setFormValues] = useState(() => createEmptyFormValues());

  const [isMobile, setIsMobile] = useState(forceMobile || window.innerWidth <= 1327);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  // const [modalOpen, setModalOpen] = useState(false); // 미사용 (CommonModal 내부 관리)
  const [resetCounter, setResetCounter] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);
  // 자기 자신 제외 프리뷰: key별 옵션/시그니처 캐시
  const [previewOptions, setPreviewOptions] = useState({}); // key -> optionsObj
  const [previewSig, setPreviewSig] = useState({}); // key -> JSON signature
  const [previewLoading, setPreviewLoading] = useState({}); // key -> boolean
  const [searchLoading, setSearchLoading] = useState(false); // 전체 필터 fetch 로딩
  const [hasLoadedOptions, setHasLoadedOptions] = useState(false);
  const [rangeAnchor, setRangeAnchor] = useState({
    startdate: null,
    spreadnights: null,
  });
  // 옵션 A: 프리뷰/오픈시 재호출 제거 (간소화)

  // 전역 라벨 캐시: 각 폼 키별 id->label 누적
  const [labelDict, setLabelDict] = useState({
    destination: {},
    departure: {},
    port: {},
    startdate: {},
    spreadnights: {},
    lineid: {},
    shipid: {},
  });

  const apiFieldToFormKey = {
    destinations: 'destination',
    departure_ports: 'departure',
    ports: 'port',
    months: 'startdate',
    spreadnights: 'spreadnights',
    cruise_lines: 'lineid',
    ship: 'shipid',
  };

  const mergeLabels = (optionsObj) => {
    if (!optionsObj) return;
    setLabelDict(prev => {
      const next = { ...prev };
      Object.entries(optionsObj).forEach(([apiField, arr]) => {
        const formKey = apiFieldToFormKey[apiField];
        if (!formKey || !Array.isArray(arr)) return;
        if (!next[formKey]) next[formKey] = {};
        arr.forEach(item => {
          const id = String(item.id);
          const label = item.label;
          if (label != null) next[formKey][id] = label;
        });
      });
      return next;
    });
  };

  const isOptionsSnapshotEmpty = (optionsObj) => (
    !optionsObj
    || Object.values(optionsObj).every(arr => Array.isArray(arr) && arr.length === 0)
  );

  const hydrateSearchOptionsFromSnapshot = (optionsObj) => {
    if (isOptionsSnapshotEmpty(optionsObj)) return;
    setSearchOptions(optionsObj);
    setHasLoadedOptions(true);
    mergeLabels(optionsObj);
  };

  // key -> API field 매핑 (옵션 소스 선택용)
  const keyToApiField = {
    destination: 'destinations',
    departure: 'departure_ports',
    port: 'ports',
    startdate: 'months',
    spreadnights: 'spreadnights',
    lineid: 'cruise_lines',
    shipid: 'ship',
  };

  // 선사 정렬: 예약 가능 그룹(TTA/DPR/OLR) 우선(id 오름차순), 나머지는 라벨 가나다/알파벳/숫자
  const sortCruiseLines = (items = []) => {
    const list = Array.isArray(items) ? [...items] : [];
    const isActive = (flag) => {
      const up = (flag || '').toString().toUpperCase();
      return up === 'TTA' || up === 'DPR' || up === 'OLR';
    };
    const actives = list
      .filter(o => isActive(o.reservation_flg))
      .sort((a, b) => Number(a.id) - Number(b.id));
    const inactives = list
      .filter(o => !isActive(o.reservation_flg))
      .sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), 'ko'));
    return [...actives, ...inactives];
  };

  // 옵션 소스 선택 규칙
  // 렌더링 규칙
  // 1) 선택 없음 → defaultOption
  // 2) 선택 있음 → searchOptions
  // 3) 단, 열려 있는 해당 항목에 '자기 자신' 선택이 있으면
  //    그 항목은 previewOptions[key] (자기 자신 제외 API 결과) 우선 사용
  const getOptionsForKey = (key) => {
    const apiField = keyToApiField[key];
    if (!apiField) return [];
    const hasAnySelection = Object.values(formValues).some(arr => Array.isArray(arr) && arr.length > 0);
    const hasOwnSelection = Array.isArray(formValues[key]) && formValues[key].length > 0;
    let source;
    if (!hasAnySelection) {
      source = defaultOption;
    } else if (openDropdown === key && hasOwnSelection) {
      // 자기 자신을 제외한 프리뷰 우선, 없으면 우선 현재 필터 결과로 표시(최소한 선택 가능한 후보는 보이게)
      source = previewOptions[key] || searchOptions;
    } else {
      source = searchOptions;
    }
    let list = source?.[apiField] || [];
    if (key === 'lineid') {
      list = sortCruiseLines(list);
    }
    if (apiField === 'spreadnights') {
      list = [...list].sort((a, b) => {
        const numA = parseInt(String(a.label || '0').replace('박', ''), 10);
        const numB = parseInt(String(b.label || '0').replace('박', ''), 10);
        return numA - numB;
      });
    }
    // 디버그: 소스 로그 (previewOptions 구분)
    // eslint-disable-next-line no-console
    if (openDropdown === key) {
      // console.log(`[SearchGroup] render options for ${key} (count: ${list.length})`);
    }
    return list;
  };

  const getFullOptionsForKey = (key) => {
    const apiField = keyToApiField[key];
    if (!apiField) return [];
    let list = defaultOption?.[apiField] || [];
    if (key === 'lineid') {
      list = sortCruiseLines(list);
    }
    if (apiField === 'spreadnights') {
      list = [...list].sort((a, b) => {
        const numA = parseInt(String(a.label || '0').replace('박', ''), 10);
        const numB = parseInt(String(b.label || '0').replace('박', ''), 10);
        return numA - numB;
      });
    }
    return list;
  };

  const getAvailableOptionsForKey = (key) => {
    const apiField = keyToApiField[key];
    if (!apiField) return [];
    let list = searchOptions?.[apiField] || [];
    if (key === 'lineid') {
      list = sortCruiseLines(list);
    }
    if (apiField === 'spreadnights') {
      list = [...list].sort((a, b) => {
        const numA = parseInt(String(a.label || '0').replace('박', ''), 10);
        const numB = parseInt(String(b.label || '0').replace('박', ''), 10);
        return numA - numB;
      });
    }
    return list;
  };

  useEffect(() => {
    setRangeAnchor(prev => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (openDropdown !== key) next[key] = null;
      });
      return next;
    });
  }, [openDropdown]);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
      // forceMobile이 true가 아닐 때만 isMobile을 자동 조정
      if (!forceMobile) {
        setIsMobile(window.innerWidth <= 1327);
      }
    };
    window.addEventListener("resize", handleResize);
    // 초기 동기화
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [forceMobile]);

  // ✅ 옵션 변경 시 API 재요청
  /**
   * 옵션 목록 조회/갱신 함수
   *
   * values: 현재 선택 상태(formValues)를 기반으로 API 파라미터를 구성합니다.
   * opts:
   *  - replaceDefault?: boolean (기본값: false)
   *    true이면 이번 응답을 기본 스냅샷(defaultOption)으로 강제로 교체합니다.
   *    사용 시점 예)
   *      - 전체 초기화 직후: 빈 선택으로 전체 옵션을 다시 받아 defaultOption 재설정
   *      - 최초 진입 시(defaultOption이 비어있을 때): 먼저 전체 옵션 스냅샷을 만들고 이후 선택 반영 조회
   */
  const fetchSearchOptions = async (values, opts = {}) => {
    try {
      setSearchLoading(true);
      const params = {};
      if (values.destination.length) params.r = values.destination.join(",");
      if (values.departure.length) params.d = values.departure.join(",");
      if (values.port.length) params.v = values.port.join(",");
      if (values.lineid.length) params.l = values.lineid.join(",");
      if (values.shipid.length) params.s = values.shipid.join(",");
      if (values.spreadnights.length) params.n = values.spreadnights.join(",");
      // m: 연-월(YYYYMM/YYYY-MM) 우선, 기존 'N월' 값은 숫자만 추출하여 하위호환
      if (values.startdate.length) {
        params.m = values.startdate
          .map(v => {
            if (typeof v === 'string' && /^\d{6}$/.test(v)) return v;        // YYYYMM
            if (typeof v === 'string' && /^\d{4}-\d{2}$/.test(v)) return v; // YYYY-MM
            if (typeof v === 'string' && v.endsWith('월')) return String(parseInt(v.replace('월',''),10));
            return String(v);
          })
          .join(",");
      }

      // 디버그 로그는 정리된 형태로 하단에서 일괄 출력

      // console.log('[SearchGroup] fetchSearchOptions params', params);
      const data = await apiGet("/search/get_search_option.php", params); // 현재 선택 조건으로 가능한 필터 옵션 묶음을 다시 요청
      // console.log('[SearchGroup] fetchSearchOptions response', data);
      const nextOptions = {
        cruise_lines: data.cruise_lines || [],
        destinations: data.destinations || [],
        departure_ports: data.departure_ports || [],
        ports: data.ports || [],
        spreadnights: data.spreadnights || [],
        months: data.months || [],
        ship: data.ship || [],
      };
      setSearchOptions(nextOptions);
      setHasLoadedOptions(true);
      // 전역 라벨 캐시 업데이트
      mergeLabels(nextOptions);

      // 아래 log helpers에서 간결하게 출력

      // 최초 호출 시 또는 강제 교체 옵션이 있을 때 defaultOption 저장
      if (opts.replaceDefault) {
        setDefaultOption(nextOptions);
      } else {
        setDefaultOption(prev => (isOptionsSnapshotEmpty(prev) ? nextOptions : prev));
      }
      // 간결 로그: 선택한 옵션 / (열려있다면) 해당 키의 선택가능 옵션
      logSelectedSummary();
      if (openDropdown) {
        logAvailableSummary(openDropdown, getOptionsForKey(openDropdown));
      }
    } catch (error) {
      console.error('옵션 조회 실패:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const requestBaseOptionsIfNeeded = async (values) => {
    if (hasLoadedOptions) return;

    if (!isOptionsSnapshotEmpty(defaultOption)) {
      hydrateSearchOptionsFromSnapshot(defaultOption);
      return;
    }

    const nextValues = values || createEmptyFormValues();
    const hasSelection = hasAnySelectedValues(nextValues);

    if (!hasSelection) {
      await fetchSearchOptions(nextValues, { replaceDefault: true });
      return;
    }

    const emptyValues = createEmptyFormValues();
    await fetchSearchOptions(emptyValues, { replaceDefault: true });
    await fetchSearchOptions(nextValues);
  };

  /**
   * 자기 자신 제외 프리뷰 옵션 조회
   *
   * values: 현재 선택 상태에서 '해당 key'만 빈 배열로 만든 값({ ...formValues, [key]: [] })
   * key   : 열려 있는 드롭다운/아코디언의 식별자(예: 'lineid', 'port' 등)
   *
   * 동작:
   *  - values를 기반으로 API 파라미터를 구성하여 get_search_option.php 호출
   *  - 응답을 previewOptions[key]에 저장하여, 열린 항목 렌더링 시 우선 사용
   *  - previewLoading[key]로 로딩 상태를 관리(로딩 중에는 '불러오는 중...' 표시)
   *  - labelDict(전역 라벨 캐시)에도 라벨을 병합하여 선택 태그가 ID로 보이지 않도록 보강
   *
   * 주의:
   *  - searchOptions는 변경하지 않음(프리뷰는 열린 항목 전용 캐시)
   *  - 동일 시그니처(같은 values)로 중복 호출을 피하기 위해 previewSig로 바깥에서 호출 제어
   */
  const fetchPreviewOptions = async (values, key) => {
    try {
      setPreviewLoading(prev => ({ ...prev, [key]: true }));
      const params = {};
      if (values.destination.length) params.r = values.destination.join(",");
      if (values.departure.length) params.d = values.departure.join(",");
      if (values.port.length) params.v = values.port.join(",");
      if (values.lineid.length) params.l = values.lineid.join(",");
      if (values.shipid.length) params.s = values.shipid.join(",");
      if (values.spreadnights.length) params.n = values.spreadnights.join(",");
      if (values.startdate.length) {
        params.m = values.startdate
          .map(v => {
            if (typeof v === 'string' && /^\d{6}$/.test(v)) return v;
            if (typeof v === 'string' && /^\d{4}-\d{2}$/.test(v)) return v;
            if (typeof v === 'string' && v.endsWith('월')) return String(parseInt(v.replace('월',''),10));
            return String(v);
          })
          .join(",");
      }
      // console.log('[SearchGroup] fetchPreviewOptions params', key, params);
      const data = await apiGet("/search/get_search_option.php", params); // 자기 자신 선택을 제외한 상태로 미리보기 옵션을 조회
      // console.log('[SearchGroup] fetchPreviewOptions response', key, data);
      const next = {
        cruise_lines: data.cruise_lines || [],
        destinations: data.destinations || [],
        departure_ports: data.departure_ports || [],
        ports: data.ports || [],
        spreadnights: data.spreadnights || [],
        months: data.months || [],
        ship: data.ship || [],
      };
      setPreviewOptions(prev => ({ ...prev, [key]: next }));
      mergeLabels(next);
    } catch (e) {
      console.error('프리뷰 옵션 불러오기 실패:', e);
    } finally {
      setPreviewLoading(prev => ({ ...prev, [key]: false }));
    }
  };


  // 최초 로딩 및 URL 변경 시 초기화
  useEffect(() => {
    // API 파라미터 키(r, d, v...)를 formValues의 키(destination, departure...)로 변환하는 맵
    const apiParamsToFormKeys = {
      r: "destination",
      d: "departure",
      v: "port",
      m: "startdate",
      n: "spreadnights",
      l: "lineid",
      s: "shipid",
    };

    // URL 파라미터를 기반으로 초기 formValues 상태를 생성합니다.
    const initialValues = createEmptyFormValues();

    // searchParams에 있는 모든 키-값 쌍을 순회
    for (const [key, value] of searchParams.entries()) {
      const formKey = apiParamsToFormKeys[key];
      if (formKey) {
        // ✅ 다중값 처리 (콤마 기준 분리)
        initialValues[formKey] = value.split(",");
      }
    }

    const hasInitialSelection = hasAnySelectedValues(initialValues);
    const isDefaultEmpty = isOptionsSnapshotEmpty(defaultOption);

    // URL 파라미터로 만든 객체로 formValues 상태를 설정
    setFormValues(initialValues);

    (async () => {
      if (hasInitialSelection) {
        if (isDefaultEmpty) {
          const emptyValues = createEmptyFormValues();
          await fetchSearchOptions(emptyValues, { replaceDefault: true });
        }
        await fetchSearchOptions(initialValues);
        return;
      }

      if (!isDefaultEmpty) {
        hydrateSearchOptionsFromSnapshot(defaultOption);
      }
    })();

  }, [searchParams]); // searchParams가 변경될 때마다 이 로직이 다시 실행

  // ✅ 드롭다운 선택 (다중 선택 지원) - 옵션 클릭 시 실행(선택 즉시 재요청)
  const handleDropdownChange = (key, value) => {
    if (RANGE_SELECT_KEYS.has(key)) {
      const normalizedValue = String(value);
      const orderedIds = getOptionsForKey(key).map(item => String(item.id));
      const anchor = rangeAnchor[key] ? String(rangeAnchor[key]) : null;

      if (!anchor) {
        setRangeAnchor(prev => ({ ...prev, [key]: normalizedValue }));
        return;
      }

      setRangeAnchor(prev => ({ ...prev, [key]: null }));
      setFormValues(prev => {
        const current = (prev[key] || []).map(v => String(v));
        let nextValues = current;
        const startIdx = orderedIds.indexOf(anchor);
        const endIdx = orderedIds.indexOf(normalizedValue);

        if (startIdx === -1 || endIdx === -1) {
          if (!current.includes(normalizedValue)) {
            nextValues = [...current, normalizedValue];
          }
        } else {
          const [from, to] = startIdx <= endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
          const rangeValues = orderedIds.slice(from, to + 1);
          nextValues = Array.from(new Set([...current, ...rangeValues]));
        }

        const next = { ...prev, [key]: nextValues };
        fetchSearchOptions(next);
        return next;
      });
      return;
    }
    setFormValues(prev => {
      if (prev[key].includes(value)) return prev; // 중복 방지
      const next = { ...prev, [key]: [...prev[key], value] };
      // 디버그: 선택 로그
      // eslint-disable-next-line no-console
      // console.log('[SearchGroup] select option:', { key, value, next });
      fetchSearchOptions(next); // 선택 추가 직후 필터링된 옵션 재요청
      return next;
    });
  };

  // ✅ 선택값 제거
  const handleOptionRemove = (key, value) => {
    setFormValues(prev => {
      const next = { ...prev, [key]: prev[key].filter(v => v !== value) };
      // 디버그: 제거 로그
      // eslint-disable-next-line no-console
      // console.log('[SearchGroup] remove option:', { key, value, next });
      fetchSearchOptions(next); // 선택 해제 후 옵션 목록 동기화
      return next;
    });
  };

  const handleDropdownToggle = (key) => {
    // 열고 닫기만 수행. 데이터 요청은 옵션 클릭시 실행
    const next = openDropdown === key ? null : key;
    setOpenDropdown(next);

    if (next === key) {
      if (!hasLoadedOptions) {
        requestBaseOptionsIfNeeded(formValues);
      }

      const hasAnySelection = hasAnySelectedValues(formValues);
      const hasOwnSelection = Array.isArray(formValues[key]) && formValues[key].length > 0;
      if (hasLoadedOptions && hasAnySelection && hasOwnSelection) {
        // 자기 자신을 제외한 프리뷰 필요
        const valuesForPreview = { ...formValues, [key]: [] };
        const sig = JSON.stringify(valuesForPreview);
        if (previewSig[key] !== sig) {
          fetchPreviewOptions(valuesForPreview, key);
          setPreviewSig(prev => ({ ...prev, [key]: sig }));
        }
      }

      // 간결 로그: 열릴 때 현재 선택/해당 키의 선택가능 옵션 요약 출력
      logSelectedSummary();
      logAvailableSummary(key, getOptionsForKey(key));
    }
    
  };

  // 아코디언에서 열림/닫힘을 명시적으로 전달
  const handleAccordionToggle = (key, opened) => {
    if (!opened) {
      setOpenDropdown(prev => (prev === key ? null : prev));
      return;
    }

    setOpenDropdown(key);

    if (!hasLoadedOptions) {
      requestBaseOptionsIfNeeded(formValues);
    }

    const hasAnySelection = hasAnySelectedValues(formValues);
    const hasOwnSelection = Array.isArray(formValues[key]) && formValues[key].length > 0;
    if (hasLoadedOptions && hasAnySelection && hasOwnSelection) {
      const valuesForPreview = { ...formValues, [key]: [] };
      const sig = JSON.stringify(valuesForPreview);
      if (previewSig[key] !== sig) {
        fetchPreviewOptions(valuesForPreview, key);
        setPreviewSig(prevSig => ({ ...prevSig, [key]: sig }));
      }
    }

    // 간결 로그: 열릴 때 요약 출력
    logSelectedSummary();
    logAvailableSummary(key, getOptionsForKey(key));
  };

  // ---- Log helpers (간결 로그 전용) ----
  const labelsFor = (key, vals) => (vals || []).map(v => labelDict[key]?.[String(v)] ?? String(v));
  const logSelectedSummary = () => {
    const summary = {};
    Object.entries(formValues).forEach(([k, vals]) => {
      if (Array.isArray(vals) && vals.length) summary[k] = labelsFor(k, vals);
    });
    // eslint-disable-next-line no-console
    // console.log('[Filter] 선택한 옵션:', summary);
  };
  const logAvailableSummary = (key, list) => {
    const labels = (list || []).map(o => o.label);
    // eslint-disable-next-line no-console
    console.log(`[Filter] ${key} 선택가능한 옵션(${labels.length}):`, labels.slice(0, 30));
  };

  const handleSearchClick = () => {
    const isEmpty = Object.values(formValues).every(arr => arr.length === 0);
    if (isEmpty) {
      alert({
        title: '검색 실패',
        content: <p>한개 이상의 옵션을 선택해 주세요.</p>,
        confirmButtonText: '닫기',
      });
      return;
    }

    // 250819 - Home.js에서 SearchGroup.js로 검색 로직 옮김
    const mapToApiParams = {
      destination: "r",
      departure: "d",
      port: "v",
      startdate: "m", // 문자열 '6월' → 숫자 6으로 변환 필요
      spreadnights: "n",
      lineid: "l",
      shipid: "s",
    };

    const params = {};

    Object.entries(formValues).forEach(([key, values]) => {
      if (values.length > 0) {
        const apiKey = mapToApiParams[key];
        if (apiKey) {
          if (key === "startdate") {
            // YYYYMM/YYYY-MM은 그대로 전달, 과거 'N월' 값은 숫자만 추출하여 전달(서버 하위호환 유지)
            const monthValues = values.map(v => {
              if (typeof v === 'string' && /^\d{6}$/.test(v)) return v;
              if (typeof v === 'string' && /^\d{4}-\d{2}$/.test(v)) return v;
              if (typeof v === 'string' && v.endsWith("월")) return String(parseInt(v.replace("월", ""), 10));
              return String(v);
            });
            params[apiKey] = monthValues.join(",");
          } else {
            params[apiKey] = values.join(",");
          }
        }
      }
    });

    const query = new URLSearchParams(params).toString();
    navigate(`/search?${query}`);

    // if (isMobile) setModalOpen(false); // 미사용
  };

  // 검색 조건 초기화
  const handleResetClick = () => {
    const emptyValues = createEmptyFormValues();
    setFormValues(emptyValues);
    // 모든 미리보기/오픈 상태 초기화
    setOpenDropdown(null);
    setPreviewOptions({});
    setPreviewSig({});
    setPreviewLoading({});
    setRangeAnchor({
      startdate: null,
      spreadnights: null,
    });
    // 기본 옵션을 현재 전체 옵션으로 재설정하면서 검색 옵션도 갱신
    fetchSearchOptions(emptyValues, { replaceDefault: true }); // 전체 초기화 후 전체 옵션 재조회
    setResetCounter(prev => prev + 1); // resetCounter 증가
  };

  const transformOptions = (
    items,
    defaultLabel = "전체",
    labelField = "label",
    fallbackField = "label",
    valueField = "id"
  ) => {
    const transformed = [];
    items.forEach((item) => {
      const id = item[valueField];
      const label = item[labelField] || item[fallbackField];
      if (id === null || id === undefined || String(id).trim() === '' || String(label ?? '').trim() === '') {
        return;
      }
      transformed.push({
        id,
        label,
        reservation_flg: (item.reservation_flg || '').toString().toUpperCase()
      });
    });
    return transformed;
  };
  
  // (정리) spreadnights 정렬은 getOptionsForKey/getFullOptionsForKey/getAvailableOptionsForKey 내부에서 처리

  const dropdownOptions = [
    {
      key: "destination",
      title: "운항 지역",
      defaultText: "지역 선택"
    },
    {
      key: "departure",
      title: "출발 항구",
      defaultText: "출발 항구 선택"
    },
    {
      key: "port",
      title: "기항지",
      defaultText: "기항지 선택"
    },
    {
      key: "startdate",
      title: "출발월",
      defaultText: "출발월 선택"
    },
    {
      key: "spreadnights",
      title: "여행 기간",
      // 정렬된 배열(sortedSpreadnights)을 사용합니다.
      defaultText: "여행 일수 선택"
    },
    {
      key: "lineid",
      title: "선사명",
      defaultText: "선사명 선택"
    },
    {
      key: "shipid",
      title: "선박명",
      defaultText: "선박명 선택"
    }

  ];

  /**
   * 로딩중인지 판별용
  */
  const isDropdownPreviewLoading = (key) => (
    key != null &&
    openDropdown === key &&
    Array.isArray(formValues[key]) &&
    formValues[key].length > 0 &&
    !!previewLoading[key]
  );

  const renderDropdowns = () => {
    // Base classes for the button
    let buttonClasses =
      "search-button w-16 h-16 rounded-lg flex items-center justify-center";
    if (openDropdown === null) {
      buttonClasses += " bg-blue-500 text-white";
    }

    return (
      <div className="search-container flex items-center">
        {dropdownOptions.map((dropdown, index) => {
          const containerStyle = { backgroundColor: openDropdown !== null && openDropdown !== dropdown.key ? "#EBEBEB" : "#FFF" };
          const isRangeKey = RANGE_SELECT_KEYS.has(dropdown.key);
          return (
            <div
              key={dropdown.key}
              className={`dropdown-container relative flex items-center ${index !== dropdownOptions.length - 1 ? "border-r border-[#DBDEE1]" : ""} ${openDropdown === dropdown.key ? 'open' : ''}`}
              style={containerStyle}
              onClick={() => handleDropdownToggle(dropdown.key)}
              role="button"
              tabIndex={0}
            >
              <SearchDropdown
                title={dropdown.title}
                options={transformOptions(getOptionsForKey(dropdown.key))}
                fullOptions={transformOptions(getFullOptionsForKey(dropdown.key))}
                availableOptions={transformOptions(getAvailableOptionsForKey(dropdown.key))}
                value={formValues[dropdown.key]}
                defaultText={dropdown.defaultText}
                onChange={(value) => handleDropdownChange(dropdown.key, value)}
                onRemove={(value) => handleOptionRemove(dropdown.key, value)}
                isOpen={openDropdown === dropdown.key}
                onToggle={() => handleDropdownToggle(dropdown.key)}
                globalLabelMap={labelDict[dropdown.key] || {}}
                isLoading={isDropdownPreviewLoading(dropdown.key)}
                isGlobalLoading={searchLoading}
                hasOtherSelection={Object.entries(formValues).some(([k, arr]) => k !== dropdown.key && Array.isArray(arr) && arr.length > 0)}
                isInitialLoading={searchLoading && !hasLoadedOptions}
                rangeMode={isRangeKey}
                rangeAnchorId={rangeAnchor[dropdown.key]}
              />
            </div>
          );
        })}
        <div className="search-button-container">
          <button className={buttonClasses} onClick={handleSearchClick}>
            <SearchW className="search-btn-icon" />검색하기
          </button>
        </div>
      </div>
    );
  };

  const renderAccordions = () => {
    // 원본 순서를 유지하기 위해 복사
    const ordered = [...dropdownOptions];

    // 열려 있는 항목이 있다면 맨 앞으로 이동 (단, 실제 뷰포트 폭이 651px **이하**일 때만)
    const shouldReorder = viewportWidth <= 651; // forceMobile은 무시하고 실제 폭만 기준
    if (shouldReorder && openDropdown) {
      const idx = ordered.findIndex((item) => item.key === openDropdown);
      if (idx > 0) {
        const [openedItem] = ordered.splice(idx, 1);
        ordered.unshift(openedItem);
      }
    }

    return (
      <div className="accordion-group">
        {ordered.map((accordion, index) => (
          <div
            key={accordion.key}
            className={`accordion-container relative flex items-center ${
              index !== ordered.length - 1 ? "border-b border-[#DBDEE1]" : ""
            }`}
          >
            <SearchAccordion
              key={accordion.key + resetCounter} // key prop 유지
              title={accordion.title}
              options={transformOptions(getOptionsForKey(accordion.key))}
              fullOptions={transformOptions(getFullOptionsForKey(accordion.key))}
              availableOptions={transformOptions(getAvailableOptionsForKey(accordion.key))}
              value={formValues[accordion.key]}
              defaultText={accordion.defaultText}
              onChange={(value) => handleDropdownChange(accordion.key, value)}
              onRemove={(value) => handleOptionRemove(accordion.key, value)}
              onOpen={() => handleDropdownToggle(accordion.key)}
              onToggle={(opened) => handleAccordionToggle(accordion.key, opened)}
              globalLabelMap={labelDict[accordion.key] || {}}
              isLoading={isDropdownPreviewLoading(accordion.key)}
              isGlobalLoading={searchLoading}
              hasOtherSelection={Object.entries(formValues).some(
                ([k, arr]) => k !== accordion.key && Array.isArray(arr) && arr.length > 0
              )}
              isInitialLoading={searchLoading && !hasLoadedOptions}
              rangeMode={RANGE_SELECT_KEYS.has(accordion.key)}
              rangeAnchorId={rangeAnchor[accordion.key]}
            />
          </div>
        ))}
      </div>
    );
  };

  // 검색 모달 아이콘 렌더함수
  const renderMobileIcon = () => {
    switch (moIconType) {
      case "reset":
        return <Reset className="search-icon" />;
      default:
        return <SearchBtn className="search-icon" />;
    }
  };

  const shouldShowLoadingOverlay = isDropdownPreviewLoading(openDropdown); // PC/TA/MO 동일하게 적용
  // const shouldShowLoadingOverlay = !isMobile && isDropdownPreviewLoading(openDropdown); // PC에서만 적용

  return (
    <section className="search" ref={ref}>
      {shouldShowLoadingOverlay && <Loading isLoading={true} />}
      {isMobile ? (
        <CommonModal
          trigger={
            <button type="button" className="mobile-search-button">
              {renderMobileIcon()}
              {moTitle}
            </button>
          }
          title={
            <>
              <SearchBtn className="search-icon" />
              검색
            </>
          }
          showFooter={true}
          customFooterContent={({ handleClose }) => (
            <div className="modal-footer-btn-group">
              <button type="button" className="btn modal-btn btn-search" onClick={() => { handleSearchClick(); handleClose(); }}>검색하기</button>
              <button type="button" className='btn modal-btn btn-search-reset' onClick={handleResetClick}>
                <ResetIcon />
                전체 초기화
              </button>
            </div>
          )}
        >
          {renderAccordions()}
        </CommonModal>
      ) : (
        renderDropdowns()
      )}
    </section>
  );
});

export default SearchGroup;
